import {
  ArrowRightIcon,
  ArrowsRightLeftIcon,
  CheckIcon,
  DocumentDuplicateIcon,
  LinkIcon,
  MegaphoneIcon,
  NewspaperIcon,
  QrCodeIcon,
  UserGroupIcon
} from '@heroicons/react/24/solid'
import clsx from 'clsx'
import PropTypes from 'prop-types'

import { COLORS, Shape } from '../primitives'
import { ExStage, Fig, LOOKS, Pose } from './stage'

const stepProp = { step: PropTypes.number.isRequired }

// Fußpunkt einer Figur → linke obere Ecke ihres Blocks
const feet = (x, y, size = 1) => ({ left: x - 22 * size, top: y - 70 * size })

const MARA = { body: 'blue', skin: '#E0AC69', hair: '#5A3825' }
const LENA = LOOKS[0]

// ---------- QR-Code als Muster, fest und ohne Bibliothek ----------

const QR = Array.from({ length: 121 }, (_, i) => {
  const x = i % 11
  const y = Math.floor(i / 11)
  const finder = (cx, cy) =>
    x >= cx &&
    x < cx + 3 &&
    y >= cy &&
    y < cy + 3 &&
    !(x === cx + 1 && y === cy + 1)
  if (finder(0, 0) || finder(8, 0) || finder(0, 8)) return true
  const v = Math.sin(i * 12.9898) * 43758.5453
  return v - Math.floor(v) > 0.52
})

function QrCode({ size = 80 }) {
  const c = size / 11
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden='true'
    >
      {QR.map((on, i) =>
        on ? (
          <rect
            key={i}
            x={(i % 11) * c}
            y={Math.floor(i / 11) * c}
            width={c}
            height={c}
            fill={COLORS.ink}
          />
        ) : null
      )}
    </svg>
  )
}

QrCode.propTypes = { size: PropTypes.number }

/** Ebene, die nur in bestimmten Schritten sichtbar ist. */
function Layer({ step, from, to = 99, className, children }) {
  const on = step >= from && step <= to
  return (
    <div
      className={clsx('ix-layer', on && 'is-on', className)}
      aria-hidden={!on}
    >
      {children}
    </div>
  )
}

Layer.propTypes = {
  ...stepProp,
  from: PropTypes.number.isRequired,
  to: PropTypes.number,
  className: PropTypes.string,
  children: PropTypes.node
}

// ---------- 1 · Messe mit QR-Code (Aivy® Attract) ----------

const MATCHES = [
  { job: 'Customer Success Manager:in', value: 91, top: true },
  { job: 'Vertrieb Innendienst', value: 76 },
  { job: 'Teamassistenz', value: 62 }
]

export function MesseScene({ step }) {
  return (
    <ExStage step={step} className='ix-stage--messe'>
      <div className='ix-floor' />

      {/* Messestand */}
      <div className='ix-booth'>
        <span className='ix-booth__logo'>aivy</span>
        <strong>Welcher Job passt zu dir?</strong>
        <span className='ix-booth__shapes'>
          <Shape kind='tri' color='yellow' size={26} />
          <Shape kind='sq' color='green' size={22} />
          <Shape kind='dot' color='pink' size={22} />
        </span>
      </div>
      <div className='ix-abs' style={feet(186, 298)}>
        <Fig {...MARA} happy={step >= 3} />
      </div>
      <div className='ix-counter'>Karriere bei uns</div>

      {/* Laufpublikum vor dem Tresen, nur solange der Stand noch leer ist */}
      <Layer step={step} from={0} to={1}>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className='ix-passerby'
            style={{
              ...feet(0, 396 - i * 8, 1.05 - i * 0.08),
              animationDelay: `${i * -3.2}s`
            }}
          >
            <Fig {...LOOKS[4 + i]} size={1.05 - i * 0.08} walk />
          </div>
        ))}
      </Layer>

      {/* QR-Aufsteller */}
      <div className='ix-qrstand'>
        <div className='ix-qrstand__sign'>
          <QrCode size={78} />
          <span>Scan mich</span>
        </div>
        <i />
      </div>

      {/* Aylin kommt zum Aufsteller */}
      <Pose
        step={step}
        className='ix-abs'
        style={feet(470, 350)}
        poses={{ 0: { x: 140 }, 1: { x: 0 } }}
      >
        <Fig {...LOOKS[1]} happy={step >= 2} walk={step === 1} />
      </Pose>

      {/* Weitere Talente stellen sich an */}
      {[0, 1].map((i) => (
        <Pose
          key={i}
          step={step}
          className='ix-abs'
          style={feet(388 + i * 48, 404, 1.1)}
          poses={{ 0: { x: 260, o: 0 }, 3: { x: 0, o: 1 } }}
          delay={0.3 + i * 0.2}
        >
          <Fig {...LOOKS[2 + i]} size={1.1} happy />
        </Pose>
      ))}

      {/* Smartphone mit Aivy® Attract */}
      <Pose
        step={step}
        className='ix-phone'
        poses={{ 0: { y: 40, s: 0.8, o: 0 }, 1: { y: 0, s: 1, o: 1 } }}
      >
        <div className='ix-phone__notch' />
        <div className='ix-phone__brand'>Aivy® Attract</div>

        <Layer step={step} from={1} to={1}>
          <div className='ix-phone__sub'>Spiel 2 von 3</div>
          <div className='ix-phone__bar'>
            <i style={{ width: '62%' }} />
          </div>
          <div className='ix-phone__game'>
            <span className='ix-hop'>
              <Shape kind='tri' color='yellow' size={30} />
            </span>
            <span className='ix-hop' style={{ animationDelay: '0.2s' }}>
              <Shape kind='sq' color='green' size={26} />
            </span>
            <span className='ix-hop' style={{ animationDelay: '0.4s' }}>
              <Shape kind='dot' color='pink' size={26} />
            </span>
          </div>
        </Layer>

        <Layer step={step} from={2} to={2}>
          <div className='ix-phone__sub'>Deine Matches</div>
          {MATCHES.map((m, i) => (
            <div
              key={m.job}
              className='ix-match'
              style={{ transitionDelay: `${0.2 + i * 0.12}s` }}
            >
              {m.top && <span className='ix-match__badge'>Passt sehr gut</span>}
              <b>{m.job}</b>
              <small>Passungs-Grad</small>
              <span className='ix-match__bar'>
                <i style={{ width: `${m.value}%` }} />
              </span>
              <span className='ix-match__more'>Mehr</span>
            </div>
          ))}
        </Layer>

        <Layer step={step} from={3}>
          <div className='ix-ad'>
            <div className='ix-ad__img'>
              <Shape kind='dot' color='pink' size={30} />
            </div>
            <small>Stellenanzeige</small>
            <b>Customer Success Manager:in</b>
            <i />
            <i />
            <i style={{ width: '60%' }} />
            <span className='ix-ad__cta'>Jetzt bewerben</span>
          </div>
        </Layer>
      </Pose>
    </ExStage>
  )
}

MesseScene.propTypes = stepProp

// ---------- 2 · Öffentlicher Einladungslink und Talentpool ----------

const CHANNELS = [
  { label: 'Stellenanzeige', Icon: NewspaperIcon, x: 26, y: 44 },
  { label: 'Social Media', Icon: MegaphoneIcon, x: 26, y: 110 },
  { label: 'Karriereseite', Icon: LinkIcon, x: 466, y: 44 },
  { label: 'Plakat & Events', Icon: QrCodeIcon, x: 466, y: 110 }
]

// 16 Plätze im Pool, die ersten 8 kommen in Schritt 2, der Rest in Schritt 3
const POOL = Array.from({ length: 16 }, (_, i) => ({
  x: 110 + (i % 8) * 60,
  y: 330 + Math.floor(i / 8) * 54,
  look: LOOKS[i % LOOKS.length],
  from: CHANNELS[i % 4],
  match: { 2: 93, 9: 88, 13: 84 }[i] || null
}))

export function LinkScene({ step }) {
  return (
    <ExStage step={step} className='ix-stage--link'>
      <svg
        className='ix-abs ix-lines'
        width='640'
        height='420'
        aria-hidden='true'
      >
        {CHANNELS.map((c) => (
          <line
            key={c.label}
            x1={c.x < 300 ? c.x + 150 : c.x}
            y1={c.y + 18}
            x2={320}
            y2={96}
            stroke='#C9D2E6'
            strokeWidth='2'
            strokeDasharray='4 6'
          />
        ))}
      </svg>

      {CHANNELS.map(({ label, Icon, x, y }) => (
        <div key={label} className='ix-channel' style={{ left: x, top: y }}>
          <Icon width={16} height={16} /> {label}
        </div>
      ))}

      <Pose
        step={step}
        className='ix-linkcard'
        poses={{ 0: { s: 1 }, 3: { x: -150, s: 0.86, o: 0 } }}
      >
        <small>Öffentlicher Einladungslink</small>
        <b>Talentpool</b>
        <div className='ix-linkcard__row'>
          <QrCode size={44} />
          <span>
            <i />
            <i style={{ width: '70%' }} />
          </span>
        </div>
      </Pose>

      <Pose
        step={step}
        className='ix-jobcard'
        poses={{ 0: { y: -20, o: 0 }, 3: { y: 0, o: 1 } }}
      >
        <small>Neue Stelle</small>
        <b>Teamleitung Support</b>
        <span className='ix-jobcard__cta'>
          <ArrowsRightLeftIcon width={14} height={14} /> Kreuzmatching
        </span>
      </Pose>

      <div className='ix-pool'>
        <span className='ix-pool__label'>
          <UserGroupIcon width={14} height={14} /> Talentpool · Ohne
          Stellenbezug
        </span>
        <Pose
          step={step}
          className='ix-pool__count'
          poses={{ 0: { o: 0 }, 2: { o: 1 } }}
        >
          16 Talente mit Stärkenprofil
        </Pose>
      </div>

      {POOL.map((p, i) => {
        const wave = i < 8 ? 1 : 2
        const startX = p.from.x + 70 - p.x
        const startY = p.from.y + 40 - p.y
        const dim = step >= 3 && !p.match
        return (
          <Pose
            key={i}
            step={step}
            className='ix-abs'
            style={feet(p.x, p.y, 0.74)}
            poses={{
              0: { x: startX, y: startY, s: 0.4, o: 0 },
              [wave]: { o: 1 },
              3: { o: dim ? 0.35 : 1, s: p.match ? 1.12 : 1 }
            }}
            delay={(i % 8) * 0.09}
          >
            <Fig {...p.look} size={0.74} happy={step >= 2} />
            {p.match && (
              <span className={clsx('ix-poolmatch', step >= 3 && 'is-on')}>
                {p.match}&nbsp;%
              </span>
            )}
          </Pose>
        )
      })}
    </ExStage>
  )
}

LinkScene.propTypes = stepProp

// ---------- 3 · Teamfit (Teamanalyse) ----------

const AXES = [
  'Emotionswahrnehmung',
  'Problemlösefähigkeit',
  'Gewissenhaftigkeit',
  'Extraversion',
  'Verträglichkeit',
  'Teamarbeit'
]
const TEAM = [0.86, 0.42, 0.38, 0.8, 0.86, 0.78]
const NEED = [0.8, 0.76, 0.74, 0.62, 0.78, 0.7]
const LENA_FIT = [0.9, 0.82, 0.84, 0.46, 0.8, 0.72]
const GAPS = [1, 2]
const R = 100
const C = { x: 150, y: 150 }

const point = (i, v) => {
  const a = -Math.PI / 2 + (i * 2 * Math.PI) / AXES.length
  return [C.x + Math.cos(a) * R * v, C.y + Math.sin(a) * R * v]
}
const poly = (vals) => vals.map((v, i) => point(i, v).join(',')).join(' ')

export function TeamScene({ step }) {
  const combined = TEAM.map((v, i) => Math.max(v, LENA_FIT[i]))
  return (
    <ExStage step={step} className='ix-stage--team'>
      <div className='ix-teamzone'>
        <span>Customer Success</span>
      </div>
      {[LOOKS[2], LOOKS[1], LOOKS[3]].map((look, i) => (
        <Pose
          key={i}
          step={step}
          className='ix-abs'
          style={feet(56 + i * 52, 362)}
          poses={{ 0: {}, 3: { x: -14 } }}
        >
          <Fig {...look} happy={step >= 3} />
        </Pose>
      ))}
      <Pose
        step={step}
        className='ix-abs'
        style={feet(200, 362)}
        poses={{ 0: { x: 60, o: 0 }, 2: { x: 46, o: 1 }, 3: { x: 0, o: 1 } }}
      >
        <Fig {...LENA} happy walk={step === 3} />
      </Pose>
      <Pose
        step={step}
        className='ix-teamtag'
        poses={{ 0: { o: 0, y: 10 }, 2: { o: 1, y: 0 } }}
      >
        Lena
      </Pose>

      <div className='ix-radar'>
        <svg width='300' height='300' viewBox='0 0 300 300' aria-hidden='true'>
          {[0.25, 0.5, 0.75, 1].map((r) => (
            <polygon
              key={r}
              points={poly(AXES.map(() => r))}
              fill='none'
              stroke='#E3E8F0'
            />
          ))}
          {AXES.map((_, i) => {
            const [x, y] = point(i, 1)
            return (
              <line key={i} x1={C.x} y1={C.y} x2={x} y2={y} stroke='#E3E8F0' />
            )
          })}
          <polygon
            className={clsx('ix-radar__shape', step >= 3 && 'is-out')}
            points={poly(TEAM)}
            fill='#DCE7FF'
            stroke={COLORS.blue}
            strokeWidth='2.5'
          />
          <polygon
            className={clsx('ix-radar__shape', step < 3 && 'is-out')}
            points={poly(combined)}
            fill='#D7F8F1'
            stroke='#2CB89C'
            strokeWidth='2.5'
          />
          <polygon
            className={clsx('ix-radar__shape', step < 1 && 'is-out')}
            points={poly(NEED)}
            fill='none'
            stroke={COLORS.ink}
            strokeWidth='2'
            strokeDasharray='5 5'
          />
          <polygon
            className={clsx('ix-radar__grow', step < 2 && 'is-out')}
            points={poly(LENA_FIT)}
            fill='none'
            stroke={COLORS.pink}
            strokeWidth='3'
          />
          {GAPS.map((i) => {
            const [x, y] = point(i, NEED[i])
            return (
              <circle
                key={i}
                className={clsx(
                  'ix-radar__gap',
                  (step < 1 || step >= 3) && 'is-out'
                )}
                cx={x}
                cy={y}
                r='9'
                fill={COLORS.pink}
              />
            )
          })}
        </svg>
        {AXES.map((a, i) => {
          const [x, y] = point(i, 1.22)
          const gap = GAPS.includes(i)
          return (
            <span
              key={a}
              className={clsx(
                'ix-radar__axis',
                gap && step >= 1 && step < 3 && 'is-gap',
                gap && step >= 3 && 'is-done'
              )}
              style={{ left: x, top: y }}
            >
              {gap && step >= 3 && <CheckIcon width={12} height={12} />}
              {a}
            </span>
          )
        })}
      </div>

      <div className='ix-legend'>
        <span>
          <i style={{ background: COLORS.blue }} /> Team
        </span>
        <span className={clsx(step < 1 && 'is-off')}>
          <i className='is-dash' /> Stellenanforderung
        </span>
        <span className={clsx(step < 2 && 'is-off')}>
          <i style={{ background: COLORS.pink }} /> Lena
        </span>
      </div>

      <Pose
        step={step}
        className='ix-callout'
        poses={{ 0: { o: 0, y: 10 }, 1: { o: 1, y: 0 }, 2: { o: 0, y: -10 } }}
      >
        Was fehlt uns noch?
      </Pose>
      <Pose
        step={step}
        className='ix-callout ix-callout--green'
        poses={{ 0: { o: 0, y: 10 }, 3: { o: 1, y: 0 } }}
      >
        Lena ergänzt das Team
      </Pose>
    </ExStage>
  )
}

TeamScene.propTypes = stepProp

// ---------- 4 · Anforderungen 2025 vs. 2026 ----------

const ROWS = [
  {
    name: 'Emotionswahrnehmung',
    y25: { ideal: [60, 85], trans: [50, 92] },
    y26: { ideal: [60, 86], trans: [50, 93] },
    trend: '='
  },
  {
    name: 'Problemlösefähigkeit',
    y25: { ideal: [38, 60], trans: [28, 70] },
    y26: { ideal: [58, 84], trans: [48, 92] },
    trend: 'up'
  },
  {
    name: 'Offenheit für Neues',
    y25: { ideal: [34, 58], trans: [24, 68] },
    y26: { ideal: [56, 82], trans: [46, 90] },
    trend: 'up'
  },
  {
    name: 'Gewissenhaftigkeit',
    y25: { ideal: [54, 80], trans: [44, 88] },
    y26: { ideal: [52, 78], trans: [42, 86] },
    trend: '='
  },
  {
    name: 'Merkfähigkeit',
    y25: { ideal: [56, 82], trans: [46, 90] },
    y26: { ideal: [38, 64], trans: [28, 74] },
    trend: 'down'
  }
]

const TREND = { up: '↑ wichtiger', down: '↓ weniger wichtig', '=': 'gleich' }

const STAFF = [
  {
    name: 'Sara M.',
    look: LOOKS[5],
    from: 88,
    to: 86,
    action: 'Passt weiter',
    tone: 'green'
  },
  {
    name: 'Ben K.',
    look: LOOKS[3],
    from: 84,
    to: 68,
    action: 'Schulung anbieten',
    tone: 'yellow'
  },
  {
    name: 'Tom B.',
    look: LOOKS[6],
    from: 79,
    to: 54,
    action: 'Gespräch führen',
    tone: 'pink'
  }
]

const span = (r) => ({ left: `${r[0]}%`, width: `${r[1] - r[0]}%` })

export function WandelScene({ step }) {
  const year = step >= 1 ? 2026 : 2025
  return (
    <ExStage step={step} className='ix-stage--wandel'>
      <div className='ix-profile'>
        <div className='ix-profile__head'>
          <div>
            <small>Anforderungsprofil</small>
            <b>Customer Success Manager:in</b>
          </div>
          <div className='ix-years'>
            <span className={clsx(year === 2025 && 'is-on')}>2025</span>
            <span className={clsx(year === 2026 && 'is-on')}>2026</span>
          </div>
        </div>
        {ROWS.map((r, i) => {
          const now = step >= 1 ? r.y26 : r.y25
          const moved = r.trend !== '='
          return (
            <div
              key={r.name}
              className={clsx('ix-row', step >= 2 && moved && 'is-moved')}
            >
              <div className='ix-row__head'>
                <span>{r.name}</span>
                <em
                  className={clsx(
                    'ix-trend',
                    `ix-trend--${r.trend === '=' ? 'same' : r.trend}`,
                    step >= 2 && 'is-on'
                  )}
                  style={{ transitionDelay: `${i * 0.08}s` }}
                >
                  {TREND[r.trend]}
                </em>
              </div>
              <div className='ix-beam'>
                <i
                  className='ix-beam__ghost'
                  style={{ ...span(r.y25.ideal), opacity: step >= 1 ? 1 : 0 }}
                />
                <i
                  className='ix-beam__trans'
                  style={{
                    ...span(now.trans),
                    transitionDelay: `${i * 0.08}s`
                  }}
                />
                <i
                  className='ix-beam__ideal'
                  style={{
                    ...span(now.ideal),
                    transitionDelay: `${i * 0.08}s`
                  }}
                />
              </div>
            </div>
          )
        })}
        <div className='ix-profile__legend'>
          <span>
            <i style={{ background: 'rgb(167 247 231)' }} /> Idealbereich
          </span>
          <span>
            <i style={{ background: 'rgb(253 218 148)' }} /> Übergangsbereich
          </span>
          <span className={clsx(step < 1 && 'is-off')}>
            <i className='is-ghost' /> 2025
          </span>
        </div>
      </div>

      {/* Fachbereiche schätzen neu ein */}
      <Layer step={step} from={0} to={2} className='ix-experts'>
        <Pose
          step={step}
          className='ix-copytag'
          poses={{ 0: { o: 0, y: 10 }, 1: { o: 1, y: 0 } }}
        >
          <DocumentDuplicateIcon width={14} height={14} /> Stelle kopiert ·
          Anforderungsanalyse 2026
        </Pose>
        {[LOOKS[2], LOOKS[1], LOOKS[3]].map((look, i) => (
          <div key={i} className='ix-abs' style={feet(470 + i * 58, 330)}>
            <Fig {...look} happy={step >= 1} />
            <Pose
              step={step}
              className='ix-check'
              poses={{ 0: { o: 0, s: 0.4 }, 1: { o: 1, s: 1 } }}
              delay={0.4 + i * 0.2}
            >
              <CheckIcon width={12} height={12} />
            </Pose>
          </div>
        ))}
      </Layer>

      {/* Bestehende Mitarbeitende gegen das neue Profil */}
      <Layer step={step} from={3} className='ix-staff'>
        <small>Mitarbeitende gegen 2026</small>
        {STAFF.map((p, i) => (
          <div
            key={p.name}
            className='ix-staffcard'
            style={{ transitionDelay: `${0.15 + i * 0.15}s` }}
          >
            <Fig {...p.look} size={0.5} happy={p.tone === 'green'} />
            <div>
              <b>{p.name}</b>
              <span className='ix-staffcard__fit'>
                {p.from} <ArrowRightIcon width={11} height={11} /> {p.to}
              </span>
            </div>
            <em className={`ix-action ix-action--${p.tone}`}>{p.action}</em>
          </div>
        ))}
      </Layer>
    </ExStage>
  )
}

WandelScene.propTypes = stepProp
